import { useRef, useState } from 'react'
import { OriginalPhoto } from '@/components/OriginalPhoto'
import { photoSrcSet } from '@/lib/albums'
import { momentPhotoUrl, useMoments, type Moment, type MomentPhoto, type MomentSummary } from '@/lib/moments'
import { tint } from '@/lib/paper'
import { cn } from '@/lib/utils'
import { MomentPin } from './MomentWall'

// A moment's photos as a post shows them: one at a time, whole, swiped or stepped through, with dots underneath.
// Each opens the original.
export function PhotoCarousel({ photos, title }: { photos: MomentPhoto[]; title: string }) {
  const strip = useRef<HTMLUListElement>(null)
  const [at, setAt] = useState(0)
  // The tallest a photo stands is set by the first, so the page doesn't jump as they're swiped through.
  const first = photos[0]!
  const ratio = Math.min(Math.max(first.width / first.height, 0.66), 1.9)
  const go = (i: number) =>
    strip.current?.children[i]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  return (
    // On a big screen it stands no taller than most of the window, so the story is never far below.
    <div className="mx-auto flex w-full flex-col gap-2.5" style={{ maxWidth: `calc(72svh * ${ratio})` }}>
      <ul
        ref={strip}
        aria-label={`Photos in ${title}`}
        onScroll={(e) => {
          const el = e.currentTarget
          setAt(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)))
        }}
        className="flex snap-x snap-mandatory [scrollbar-width:none] overflow-x-auto overscroll-x-contain rounded-3xl"
        style={{ aspectRatio: String(ratio) }}
      >
        {photos.map((photo, i) => {
          const name = `Photo ${i + 1} of ${photos.length} in ${title}`
          return (
            <li key={photo.id} className={cn('relative w-full shrink-0 snap-center', tint(i))}>
              <OriginalPhoto
                src={momentPhotoUrl(photo.id, 'original')}
                fallback={momentPhotoUrl(photo.id, 'display')}
                alt={name}
              >
                <button
                  type="button"
                  aria-label={`Open ${name.charAt(0).toLowerCase()}${name.slice(1)}`}
                  className="block size-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/60 focus-visible:ring-inset"
                >
                  <img
                    src={momentPhotoUrl(photo.id, 'medium')}
                    srcSet={photoSrcSet({ ...photo, ref: photo.id }, ['thumb', 'medium', 'display'], momentPhotoUrl)}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    alt=""
                    decoding="async"
                    loading={i === 0 ? 'eager' : 'lazy'}
                    className="size-full object-contain"
                  />
                </button>
              </OriginalPhoto>
            </li>
          )
        })}
      </ul>
      {photos.length > 1 && (
        <div className="flex justify-center gap-1.5">
          {photos.map((photo, i) => (
            <button
              key={photo.id}
              type="button"
              aria-label={`Show photo ${i + 1}`}
              aria-current={i === at}
              onClick={() => go(i)}
              className="grid size-6 place-items-center rounded-full outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <span
                className={cn(
                  'size-1.5 rounded-full transition-[background-color,transform] duration-200',
                  i === at ? 'scale-125 bg-brand' : 'bg-border',
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// A few words set large, the way a Words moment reads on its own page.
export function BigWords({ words }: { words: string }) {
  return (
    <blockquote className="flex flex-col gap-5 rounded-3xl bg-secondary px-6 py-8 sm:px-10 sm:py-12">
      <span aria-hidden className="bg-logo h-1 w-10 rounded-full" />
      <p className="text-[1.6rem] leading-tight font-medium tracking-tight text-pretty whitespace-pre-line sm:text-[2rem]">
        {words}
      </p>
    </blockquote>
  )
}

// What a moment leads to: others from the same year, and more with the first person who was there.
export function MoreMoments({ moment }: { moment: Moment }) {
  const year = moment.happenedOn?.slice(0, 4)
  const person = moment.people[0]
  const sameYear = useMoments(moment.familyId, false, undefined, { year }, !!year)
  const withThem = useMoments(moment.familyId, false, undefined, { person: person?.id }, !!person)
  const others = (list: ReturnType<typeof useMoments>) =>
    (list.data?.pages[0]?.moments ?? []).filter((x) => x.id !== moment.id).slice(0, 8)
  const fromYear = year ? others(sameYear) : []
  const seen = new Set(fromYear.map((x) => x.id))
  const more = person ? others(withThem).filter((x) => !seen.has(x.id)) : []
  return (
    <>
      {fromYear.length > 0 && <Row title={`Also from ${year}`} moments={fromYear} />}
      {person && more.length > 0 && <Row title={`More with ${person.name}`} moments={more} />}
    </>
  )
}

function Row({ title, moments }: { title: string; moments: MomentSummary[] }) {
  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      <h2 className="text-title font-semibold tracking-tight">{title}</h2>
      <ul className="-mx-5 flex snap-x [scrollbar-width:none] gap-3 overflow-x-auto px-5 pb-2 sm:-mx-8 sm:px-8 nav:mx-0 nav:grid nav:grid-cols-3 nav:px-0">
        {moments.map((x, i) => (
          <li key={x.id} className="w-40 shrink-0 snap-start nav:w-auto">
            <MomentPin moment={x} index={i} />
          </li>
        ))}
      </ul>
    </section>
  )
}
